/**
 * Every overlay on the Database screen opens the same way: a panel sliding in
 * from the right edge over a fading backdrop — or, for the object sheet on a
 * phone, rising from the bottom.
 *
 * One component so the motion, the Escape key, the click outside and the
 * support launcher stepping aside are the same for a row, a new column, a new
 * table and a DROP. It owns its own exit: when `open` turns false it keeps the
 * last content on screen for the length of the motion (`.is-closing` in
 * index.css) and only then unmounts — the caller just flips a boolean, and
 * the content that was showing does not vanish mid-slide because the state
 * behind it was cleared.
 */
import { useEffect, useRef, useState } from 'react';
import { DsButton } from '../../components/ds';

/** How long the panel plays its way out — `.is-closing` in index.css. */
const CLOSE_MS = 180;

/** Open drawers, so the launcher comes back only when the last one closes. */
let openCount = 0;

const SideDrawer = ({ open, onClose, side = 'right', width = 'md:w-[480px]', zIndex = 'z-[60]', children }: {
  open: boolean,
  onClose: () => void,
  side?: 'right' | 'bottom',
  /** Tailwind width of the right-hand panel from `md` up. */
  width?: string,
  zIndex?: string,
  children: React.ReactNode,
}) => {
  const [isMounted, setIsMounted] = useState(open);
  const [isClosing, setIsClosing] = useState(false);
  const lastChildren = useRef(children);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  if (open) {
    lastChildren.current = children;
  }

  useEffect(() => {
    if (open) {
      setIsClosing(false);
      setIsMounted(true);
      return;
    }

    if (!isMounted) {
      return;
    }

    setIsClosing(true);
    const timer = window.setTimeout(() => {
      setIsMounted(false);
      setIsClosing(false);
    }, CLOSE_MS);

    return () => window.clearTimeout(timer);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // The support launcher sits on a drawer's buttons; it steps out while one
  // is open (`b4a2-overlay-open` in index.css).
  useEffect(() => {
    if (!open) {
      return;
    }

    openCount += 1;
    document.body.classList.add('b4a2-overlay-open');

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) {
        document.body.classList.remove('b4a2-overlay-open');
      }
    };
  }, [open]);

  if (!isMounted) {
    return null;
  }

  return (
    <div
      className={[
        'b4-db-backdrop fixed inset-0 flex bg-navy-950/60 backdrop-blur-md',
        side === 'bottom' ? 'flex-col' : 'justify-end',
        isClosing ? 'is-closing' : '',
        zIndex,
      ].join(' ')}
      onClick={() => onCloseRef.current()}
    >
      <div
        className={side === 'bottom'
          ? 'b4-db-sheet mt-auto h-[88vh] w-full flex flex-col bg-navy-800 border-t border-navy-500 rounded-t-container overflow-hidden'
          : `b4-db-drawer h-full w-full ${width} max-w-full`}
        onClick={event => event.stopPropagation()}
      >
        {open ? children : lastChildren.current}
      </div>
    </div>
  );
};

/**
 * The inside of a right-hand drawer: the eyebrow, title and one line of what
 * it does, a Close button, a body that scrolls, and a footer that stays in
 * reach however long the body gets — a summary on the left, the buttons on
 * the right with the safe one first.
 */
export const DrawerPanel = ({ eyebrow, title, blurb, onClose, summary, footer, children }: {
  eyebrow?: React.ReactNode,
  title: React.ReactNode,
  blurb?: React.ReactNode,
  onClose: () => void,
  /** The dim line on the left of the footer. */
  summary?: React.ReactNode,
  footer?: React.ReactNode,
  children: React.ReactNode,
}) => (
  <div className="flex flex-col h-full bg-navy-800 border-l border-navy-500 shadow-[0_24px_64px_rgba(0,0,0,.55)]">
    <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-navy-700">
      <div className="flex flex-col gap-1.5 min-w-0">
        {eyebrow ? <span className="b4-eyebrow">{eyebrow}</span> : null}
        <h2 className="m-0 font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white truncate">
          {title}
        </h2>
        {blurb ? <p className="m-0 text-ds-135 leading-[1.55] text-navy-200">{blurb}</p> : null}
      </div>
      <DsButton variant="ghost" size={touchSize()} onClick={onClose}>Close</DsButton>
    </div>

    <div className="flex-1 min-h-0 overflow-auto b4-thin px-6 py-5">{children}</div>

    {footer || summary ? (
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-navy-700">
        {summary ? <span className="font-courrier text-ds-115 text-navy-300">{summary}</span> : null}
        {footer}
      </div>
    ) : null}
  </div>
);

/** 44px controls on a phone, the desktop 38px above it. */
export const touchSize = () =>
  (typeof window !== 'undefined' && window.innerWidth < 720 ? 'touch' : 'lg');

/** `ds-field` — a label, the control, and the hint under it. */
export const Field = ({ label, isRequired, hint, children }: {
  label: string,
  isRequired?: boolean,
  hint?: React.ReactNode,
  children: React.ReactNode,
}) => (
  <label className="flex flex-col gap-1.5 w-full min-w-0">
    <span className="text-ds-135 font-medium leading-[1.4] text-white">
      {label}
      {isRequired ? <span className="text-navy-300"> *</span> : null}
    </span>
    {children}
    {hint ? <span className="text-ds-115 leading-[1.5] text-navy-200">{hint}</span> : null}
  </label>
);

export const inputClass =
  'b4-focus w-full h-11 lg:h-[38px] rounded-control border border-navy-400 bg-navy-800 px-3 text-[16px] lg:text-ds-135 text-white placeholder:text-navy-300 hover:border-navy-300 focus:border-action focus:shadow-[0_0_0_3px_rgba(32,138,236,.25)] outline-none transition-[border-color,box-shadow] duration-[120ms]';

export default SideDrawer;
